<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <div class="d-flex justify-space-between align-center mb-4">
          <h1 class="text-h4 mb-0">{{ $t('expenseTypes.title') }}</h1>
          <v-btn color="primary" @click="openAddDialog">{{ $t('expenseTypes.add') }}</v-btn>
        </div>
      </v-col>
    </v-row>
    
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title>{{ $t('expenseTypes.list') }}</v-card-title>
          <v-card-text>
            <v-table density="compact">
              <thead>
                <tr>
                  <th>{{ $t('expenseTypes.id') }}</th>
                  <th>{{ $t('expenseTypes.name') }}</th>
                  <th>{{ $t('common.actions') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="expenseType in expensesTypes" :key="expenseType.id">
                  <td>{{ expenseType.id }}</td>
                  <td><strong>{{ expenseType.name }}</strong></td>
                  <td>
                    <v-btn variant="plain" size="small" @click="editExpenseType(expenseType)" icon="mdi-pencil"></v-btn>
                  </td>
                </tr>
                <tr v-if="expensesTypes.length === 0">
                  <td colspan="3" class="text-center text-grey">{{ $t('expenseTypes.noExpenseTypesFound') }}</td>
                </tr>
              </tbody>
            </v-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="showAddDialog" max-width="500" @update:model-value="(val) => { if (!val) resetForm(); }">
      <v-card>
        <v-card-title>{{ expenseTypeForm.id ? $t('expenseTypes.edit') : $t('expenseTypes.add') }}</v-card-title>
        <v-card-text>
          <v-form>
            <v-text-field
              v-model="expenseTypeForm.name"
              :label="$t('expenseTypes.name')"
              :placeholder="$t('expenseTypes.namePlaceholder')"
              required
              :rules="[v => !!v || $t('expenseTypes.nameRequired')]"
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="showAddDialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="primary" @click="saveExpenseType">{{ expenseTypeForm.id ? $t('common.save') : $t('common.save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showErrorDialog" max-width="500">
      <v-card>
        <v-card-title class="text-h6 text-error">
          <v-icon color="error" class="mr-2">mdi-alert-circle</v-icon>
          {{ $t('errors.unknownError') }}
        </v-card-title>
        <v-card-text>
          <p class="text-body-1">{{ errorMessage }}</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="showErrorDialog = false">{{ $t('errors.ok') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { apiGet, apiPost, apiPut } from '../utils/api'

const { t } = useI18n()

interface ExpensesType {
  id: number
  name: string
}

const expensesTypes = ref<ExpensesType[]>([])
const showAddDialog = ref(false)
const showErrorDialog = ref(false)
const errorMessage = ref('')
const expenseTypeForm = ref({
  id: null as number | null,
  name: ''
})

const loadExpensesTypes = async () => {
  try {
    const response = await apiGet('/api/expenses-types')
    if (response.ok) {
      expensesTypes.value = await response.json()
    }
  } catch (error) {
    console.error('Load expenses types error:', error)
  }
}

const openAddDialog = () => {
  resetForm()
  showAddDialog.value = true
}

const editExpenseType = (expenseType: ExpensesType) => {
  expenseTypeForm.value = {
    id: expenseType.id,
    name: expenseType.name
  }
  showAddDialog.value = true
}

const resetForm = () => {
  expenseTypeForm.value = {
    id: null,
    name: ''
  }
}

const showError = (message: string) => {
  errorMessage.value = message
  showErrorDialog.value = true
}

const saveExpenseType = async () => {
  if (!expenseTypeForm.value.name) {
    showError(t('expenseTypes.fillName'))
    return
  }

  try {
    let response
    if (expenseTypeForm.value.id) {
      // Update existing expense type
      response = await apiPut(`/api/expenses-types/${expenseTypeForm.value.id}`, { expenses_type: expenseTypeForm.value })
    } else {
      // Create new expense type
      response = await apiPost('/api/expenses-types', { expenses_type: expenseTypeForm.value })
    }

    if (response.ok) {
      await loadExpensesTypes()
      showAddDialog.value = false
      resetForm()
    } else {
      const errorData = await response.json()
      const errorText = errorData.errors?.join(', ') || t('errors.unknownError')
      showError(t('expenseTypes.failedToSave', { errors: errorText }))
    }
  } catch (error) {
    console.error('Save expense type error:', error)
    const errorText = error instanceof Error ? error.message : t('expenseTypes.errorSaving', { errors: error.message })
    showError(t('expenseTypes.errorSaving', { errors: errorText }))
  }
}

onMounted(() => {
  loadExpensesTypes()
})
</script>
